import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Counter } from '@/components/form/fields/number/Counter';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { Form } from '@/components/form/Form';
import { getCategoryIcon } from '@/components/icons/CategoryIcons';
import { useModal } from '@/components/modal/useModal';
import InfoTooltip from '@/components/tooltip/InfoTooltip';
import UnsavedChanges from '@/components/unsavedChanges/UnsavedChanges';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { ServiceRequestLayout } from '@/layouts/ServiceRequestLayout';
import { HaloEquipmentDto } from '@/services/api/contracts/companies';
import { useCategories, useGetRateCards } from '@/services/api/hooks/catalog';
import { useCompany, useGetCompanyHaloEquipment } from '@/services/api/hooks/companies';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { resolveReturnOverrideRateCardId, resolveReturnProcessingRateCardId } from '@/utils/categoryOverrides';
import { formatCurrency } from '@/utils/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { PackagePlus } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaHeadset } from 'react-icons/fa';
import { FiCheck, FiGlobe, FiPackage, FiSettings, FiShield } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useSandbox } from '../../contexts/SandboxContext';
import { OffboardUserCollectionModal } from './OffboardUserCollectionModal';

const HardwareCollectionSection: FC<{
  triggerTypes: string[];
  deviceTriggers: string[];
  peripheralTriggers: string[];
  handleTriggerChange: (t: string, qty: number, field: any) => void;
  isTypeSerialized: (t: string) => boolean;
  methods: any;
}> = ({ triggerTypes, deviceTriggers, peripheralTriggers, handleTriggerChange, isTypeSerialized, methods }) => {
  const renderTriggerRow = (t: string) => {
    const qty = methods.watch(t) || 0;
    const isSerialized = isTypeSerialized(t);
    const showTable = isSerialized && qty > 0;

    return (
      <div key={t} className="flex flex-col border-b last:border-b-0">
        <div className="flex flex-row items-center justify-between py-2 px-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-4">
            <div className="text-2xl text-gray-500 w-8 flex justify-center">
              {getCategoryIcon(t)}
            </div>
            <span className="font-medium capitalize text-gray-700">{t}</span>
          </div>
          <Controller
            name={t}
            control={methods.control}
            render={({ field }) => (
              <Counter 
                {...field} 
                id={t}
                onChange={(val) => handleTriggerChange(t, val, field)} 
                min={0}
              />
            )}
          />
        </div>
        {showTable && (
          <div className="px-4 pb-4">
            <table className="w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Make / Model <span className="text-red-500">*</span></th>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Serial Number <span className="text-red-500">*</span></th>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Asset Tag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {Array.from({ length: qty }).map((_, i) => (
                  <tr key={i}>
                    <td className="p-1 border-r border-gray-200">
                      <Controller
                        name={`assets.${t}.${i}.makeModel`}
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <input
                              {...field}
                              className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : ''}`}
                              placeholder="Make/Model"
                            />
                          </div>
                        )}
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200">
                      <Controller
                        name={`assets.${t}.${i}.serialNumber`}
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <input
                              {...field}
                              className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : ''}`}
                              placeholder="Serial Number"
                            />
                          </div>
                        )}
                      />
                    </td>
                    <td className="p-1">
                      <Controller
                        name={`assets.${t}.${i}.assetTag`}
                        control={methods.control}
                        render={({ field }) => (
                          <input
                            {...field}
                            className="w-full bg-transparent border-0 focus:ring-0 px-2 py-1 placeholder:italic placeholder:text-gray-400"
                            placeholder="Optional"
                          />
                        )}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded bg-white overflow-hidden">
        {deviceTriggers.length > 0 && (
          <div>
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <h5 className="text-sm font-semibold text-gray-500 uppercase">Devices</h5>
            </div>
            <div className="divide-y divide-gray-200 border-b border-gray-200">
              {deviceTriggers.map(renderTriggerRow)}
            </div>
          </div>
        )}
        
        {peripheralTriggers.length > 0 && (
          <div>
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <h5 className="text-sm font-semibold text-gray-500 uppercase">Peripherals</h5>
            </div>
            <div className="divide-y divide-gray-200">
              {peripheralTriggers.map(renderTriggerRow)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getAdminTooltipText = (companyName: string) => (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    {/* Card 1: Ticket Labour */}
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FaHeadset className="w-4 h-4 text-[#41D2F1]" />
        Ticket Labour
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <span className="text-gray-700">Create user (If PAYG Model)</span>
        </li>
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <div className="text-gray-700 w-full">
            <span className="block mb-2">Comms - Inform user:</span>
            <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Returns packaging is on the way from {companyName}
              </li>
            </ul>
          </div>
        </li>
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <div className="text-gray-700 w-full">
            <span className="block mb-2">Instructions:</span>
            <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                How to package the equipment
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                How to book home/office collection at user's convenience
              </li>
            </ul>
          </div>
        </li>
        {[
          "Chaser 1 - Email",
          "Chaser 2 - Email",
          "Chaser 3 - Phone call",
          "Receive goods on system",
          "Check serials match",
          "Check and apply customer lifecycle policies",
          "Assign to correct processing path",
          "Unassign products from user",
          "Remove user from system",
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>
    </div>

    {/* Card 2: Base Pack */}
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FiPackage className="w-4 h-4 text-[#EB6626]" />
        Base (1st item) Pack
      </h6>
      <ul className="space-y-2">
        {[
          "Size correct packaging for requirement",
          "Cost of packaging and packing materials",
          "Build packaging and add internal packing materials",
        ].map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const logisticsTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiGlobe className="w-5 h-5 text-[#41D2F1] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        For US and EU regions, there is one average logistics cost to cover the whole region. This gives predictable costs and reduces admin.
      </p>
    </div>
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiShield className="w-5 h-5 text-[#EB6626] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        All goods are insured for their full amount whilst in transit and in our warehousing locations.
      </p>
    </div>
  </div>
);

const processingTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FiSettings className="w-4 h-4 text-[#EB6626]" />
        Goods Receiving Fee
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <div className="text-gray-700 w-full">
            <span className="block mb-2">Goods receiving process:</span>
            <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Scan goods in
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Cross check serials to match
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Allocate unique stocking ID
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Shelve
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <PackagePlus className="w-4 h-4 text-[#41D2F1]" />
        Additional Items - Offboard
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <span className="text-gray-700">Additional items added to consignment</span>
        </li>
      </ul>
    </div>
  </div>
);

export const OffboardUserPage: FC = () => {
  const { partnerId = '', companyId = '' } = useParams<{ partnerId: string; companyId?: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.roles?.some((r: any) => r.type === RoleType.Admin);
  const { isSandboxMode, setTelemetry } = useSandbox();

  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Offboard User',
      to: ''
    }
  ];

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);
  const [rateCards] = useGetRateCards('OffboardUser', partnerId, companyId);
  const [equipmentPayload] = useGetCompanyHaloEquipment(partnerId, companyId);
  const [categories] = useCategories();

  const filteredRateCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('OffboardUser')) || [];
  }, [rateCards]);

  type UserOption = { name: string; email: string | null; rawValue: string };

  const uniqueUsers = useMemo<UserOption[]>(() => {
    if (!equipmentPayload) return [];
    const rawUsers = Array.from(new Set(equipmentPayload.map((e: HaloEquipmentDto) => e.user))).filter(Boolean) as string[];
    
    return rawUsers.map(raw => {
      const parts = raw.split('|').map(p => p.trim());
      return {
        name: parts[0] || raw,
        email: parts.length > 1 && parts[1].includes('@') ? parts[1] : null,
        rawValue: raw
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [equipmentPayload]);

  const triggerTypes = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter(c => !c.hideFromGenerics)
      .map(c => c.name)
      .sort();
  }, [categories]);

  const { deviceTriggers, peripheralTriggers } = useMemo(() => {
    if (!categories) return { deviceTriggers: [], peripheralTriggers: [] };
    
    const devices = new Set<string>();
    const peripherals = new Set<string>();
    
    categories.forEach(c => {
      if (c.hideFromGenerics) return;
      if (c.isSerialized) {
        devices.add(c.name);
      } else {
        peripherals.add(c.name);
      }
    });

    return {
      deviceTriggers: triggerTypes.filter(t => devices.has(t)),
      peripheralTriggers: triggerTypes.filter(t => peripherals.has(t))
    };
  }, [categories, triggerTypes]);

  const isTypeSerialized = (t: string) => {
    return categories?.find(c => c.name === t)?.isSerialized || false;
  };

  const schema = useMemo(() => {
    const shape: any = {};
    
    shape.userName = z.string().min(1, 'Required');
    shape.userEmail = z.string().email('Invalid email').optional().or(z.literal('na')).or(z.literal(''));
    shape.personalEmail = z.string().min(1, 'Required').email('Invalid email');
    shape.userMobile = z.string().min(1, 'Required');
    shape.noCorporateEmail = z.boolean().default(false);

    // Add counters for trigger types
    triggerTypes.forEach((t) => {
      shape[t] = z.number().min(0).default(0);
    });

    shape.assets = z.record(
      z.string(),
      z.array(
        z.object({
          makeModel: z.string().min(1, 'Make/Model is required'),
          serialNumber: z.string().min(1, 'Serial number is required'),
          assetTag: z.string().optional()
        })
      )
    ).default({});

    return z.object(shape).catchall(z.any()).superRefine((data, ctx) => {
      const seenSns = new Set<string>();
      
      triggerTypes.forEach((t) => {
        const qty = (data[t] as number) || 0;
        if (qty > 0 && isTypeSerialized(t)) {
          const catAssets = data.assets?.[t] || [];
          for (let i = 0; i < qty; i++) {
            const asset = catAssets[i];
            if (!asset) continue;
            
            const sn = asset.serialNumber;
            if (!sn || typeof sn !== 'string' || sn.trim() === '') {
              // Validated by object schema
            } else {
              const normalizedSn = sn.toLowerCase().trim();
              if (seenSns.has(normalizedSn)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Serial numbers must be unique', path: ['assets', t, i, 'serialNumber'] });
              }
              seenSns.add(normalizedSn);
            }
          }
        }
      });
    });
  }, [triggerTypes, categories]);

  type FormFields = z.infer<typeof schema>;

  const methods = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      macroAction: null,
      userName: '',
      userEmail: '',
      personalEmail: '',
      noCorporateEmail: false
    }
  });

  useEffect(() => {
    if (triggerTypes.length > 0) {
      const defaults: any = { macroAction: null };
      triggerTypes.forEach(t => {
        defaults[t] = 0;
      });
      defaults.assets = {};
      defaults.userName = '';
      defaults.userEmail = '';
      defaults.personalEmail = '';
      defaults.noCorporateEmail = false;
      methods.reset(defaults);
    }
  }, [triggerTypes, methods]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const modalProps = useModal();
  const [checkoutPayload, setCheckoutPayload] = useState<any>(null);
  const [selectedUserEquipment, setSelectedUserEquipment] = useState<HaloEquipmentDto[]>([]);

  const triggerValues = methods.watch(triggerTypes as any[]);

  const getMaxAllowedCollectionWeight = () => {
    const tierCards = filteredRateCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride).sort((a, b) => (a.propertyBag.maxChargeableWeightKg || 0) - (b.propertyBag.maxChargeableWeightKg || 0));
    if (tierCards.length > 0) {
      return tierCards[tierCards.length - 1].propertyBag.maxChargeableWeightKg!;
    }
    return Infinity;
  };

  const simulateCollectionWeight = (proposedHardwareValues: Record<string, number>) => {
    let totalDeadWeight = 0;
    let totalVolumetricWeight = 0;

    Object.entries(proposedHardwareValues).forEach(([itemType, qty]) => {
      if (qty <= 0) return;
      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = itemType.toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      if (category) {
        const overrideRateCardId = resolveReturnOverrideRateCardId(category, company?.region?.id);
        if (overrideRateCardId) {
          return; // Skip overrides!
        }
        for (let i = 0; i < qty; i++) {
          totalDeadWeight += (category.logisticsConfig?.return?.averageWeightKg || 0);
          const baseVolWeight = (category.logisticsConfig?.return?.dimensionalWeightKg || 0);
          const packingEff = category.logisticsConfig?.return?.packingEfficiencyPercent || 100;
          const packedVolWeight = baseVolWeight / (packingEff / 100);
          totalVolumetricWeight += packedVolWeight;
        }
      }
    });

    return Math.max(totalDeadWeight, totalVolumetricWeight);
  };

  const handleTriggerChange = (t: string, qty: number, field: any) => {
    const currentFormValues = methods.getValues();
    const currentQty = currentFormValues[t] as number || 0;
    if (qty > currentQty) {
      const proposed = { ...currentFormValues, [t]: qty } as Record<string, number>;
      if (simulateCollectionWeight(proposed) > getMaxAllowedCollectionWeight()) {
        toast.error('This will exceed a single courier consignment limit, please contact your account manager for a bespoke quotation', {
          className: '!border-2 !border-secondary-600'
        });
        return;
      }
    }
    
    if (isTypeSerialized(t)) {
      const currentAssets = (currentFormValues.assets as Record<string, any[]>)?.[t] || [];
      const newAssets = [...currentAssets];
      if (qty > currentQty) {
        for (let i = currentQty; i < qty; i++) {
          newAssets.push({ makeModel: '', serialNumber: '', assetTag: '' });
        }
      } else if (qty < currentQty) {
        newAssets.splice(qty);
      }
      methods.setValue(`assets.${t}` as any, newAssets, { shouldValidate: true, shouldDirty: true });
    }

    field.onChange(qty);
  };

  const { inferredServices, runningTotal, telemetryData } = useMemo(() => {
    let total = 0;
    const services: Array<any> = [];
    const collectionItemLog: any[] = [];
    if (!filteredRateCards) return { inferredServices: services, runningTotal: total, telemetryData: { collection: { itemLog: [], totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] } } };

    // --- COLLECTION CALCULATION ---
    const pendingItems: Array<{ type: string, hasLifecyclePack: boolean, isCollectionItem: boolean, serial?: string, rawItem?: any }> = [];
    triggerTypes.forEach((t, index) => {
      const qty = (triggerValues[index] as number) || 0;
      if (qty > 0) {
        // Filter user equipment for this trigger type
        const matchedEquipment = selectedUserEquipment.filter(item => {
          let normalizedType = item.type?.toLowerCase().trim() || '';
          if (normalizedType.endsWith('s')) normalizedType = normalizedType.slice(0, -1);
          let nt = t.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return nt === normalizedType;
        });
        
        let lifecyclePackCount = matchedEquipment.filter(e => e.hasLifecyclePack).length;

        for (let i = 0; i < qty; i++) {
          if (lifecyclePackCount > 0) {
            pendingItems.push({ type: t, hasLifecyclePack: true, isCollectionItem: true });
            lifecyclePackCount--;
          } else {
            pendingItems.push({ type: t, hasLifecyclePack: false, isCollectionItem: true });
          }
        }
      }
    });

    const hasItems = pendingItems.length > 0;
    if (!hasItems) return { inferredServices: services, runningTotal: total, telemetryData: { collection: { itemLog: [], totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] } } };

    // Pass 1: The Single OffboardUser Base Pack
    const basePack = filteredRateCards.find(c => c.propertyBag.basePackForRequestType === 'OffboardUser' || c.propertyBag.basePackForRequestType === 'Offboard');
    if (basePack) {
      const rawPrice = basePack.price;
      const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
      const price = priceInPence / 100;
      services.push({ rateCard: basePack, name: basePack.name || 'Offboard Base Pack', quantity: 1, labels: ['Offboard Base Pack'], isInventoryItem: false, price, total: price, itemType: 'Service', listSection: 'admin' });
      total += price;
    }

    // Pass 2.5: Override Extract (Collection)
    const overriddenIndexes = new Set<number>();
    pendingItems.forEach((item, index) => {
      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = item.type.toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      const overrideRateCardId = resolveReturnOverrideRateCardId(category, company?.region?.id);
      if (overrideRateCardId) {
        overriddenIndexes.add(index);
        const overrideCard = filteredRateCards.find(r => r.id === overrideRateCardId);
        if (overrideCard) {
          const rawPrice = overrideCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;
          services.push({
            rateCard: overrideCard,
            name: overrideCard.name || 'Crate Fee',
            quantity: 1,
            labels: [overrideCard.name || 'Crate Fee'],
            hasLifecyclePack: item.hasLifecyclePack,
            associatedPhysicalItems: [{ ...item, suppressSerialInput: true }],
            isInventoryItem: false,
            price,
            total: price,
            itemType: 'Service',
            listSection: 'logistics'
          });
          total += price;
        }
      }
    });

    // Pass 2: Per-Item Handling (Collection)
    const specificHandlingGroups: Record<string, { items: typeof pendingItems }> = {};
    const genericFallbackItems: typeof pendingItems = [];

    pendingItems.forEach(item => {
      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = item.type.toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      const itemProcessingRateCardId = resolveReturnProcessingRateCardId(category, company?.region?.id);

      if (itemProcessingRateCardId) {
        if (!specificHandlingGroups[itemProcessingRateCardId]) {
          specificHandlingGroups[itemProcessingRateCardId] = { items: [] };
        }
        specificHandlingGroups[itemProcessingRateCardId].items.push(item);
      } else {
        genericFallbackItems.push(item);
      }
    });

    Object.entries(specificHandlingGroups).forEach(([rateCardId, group]) => {
      const specificCard = filteredRateCards.find(r => r.id === rateCardId);
      if (specificCard && group.items.length > 0) {
        const rawPrice = specificCard.price;
        const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
        const price = priceInPence / 100;
        const qty = group.items.length;

        services.push({
          rateCard: specificCard,
          name: specificCard.name,
          quantity: qty,
          labels: group.items.map(i => i.type),
          hasLifecyclePack: group.items.some(i => i.hasLifecyclePack),
          associatedPhysicalItems: group.items,
          isInventoryItem: false,
          price,
          total: price * qty,
          itemType: 'Service',
          listSection: 'processing'
        });
        total += price * qty;
      }
    });

    if (genericFallbackItems.length > 0) {
      const handlingCard = filteredRateCards.find(c => c.propertyBag.itemRateForRequestType === 'OffboardUser' || c.propertyBag.itemRateForRequestType === 'Offboard');
      if (handlingCard) {
        const lifecyclePackItems = genericFallbackItems.filter(i => i.hasLifecyclePack);
        const standardItems = genericFallbackItems.filter(i => !i.hasLifecyclePack);
        const rawPrice = handlingCard.price;
        const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
        const price = priceInPence / 100;

        if (lifecyclePackItems.length > 0) {
          services.push({ 
            rateCard: handlingCard, 
            name: handlingCard.name || 'Handling',
            quantity: lifecyclePackItems.length, 
            labels: lifecyclePackItems.map(i => i.type), 
            hasLifecyclePack: true,
            associatedPhysicalItems: lifecyclePackItems,
            isInventoryItem: false,
            price,
            total: price * lifecyclePackItems.length,
            itemType: 'Service',
            listSection: 'processing'
          });
          total += price * lifecyclePackItems.length;
        }
        if (standardItems.length > 0) {
          services.push({ 
            rateCard: handlingCard, 
            name: handlingCard.name || 'Handling',
            quantity: standardItems.length, 
            labels: standardItems.map(i => i.type), 
            hasLifecyclePack: false,
            associatedPhysicalItems: standardItems,
            isInventoryItem: false,
            price,
            total: price * standardItems.length,
            itemType: 'Service',
            listSection: 'processing'
          });
          total += price * standardItems.length;
        }
      }
    }

    // Pass 3: Volumetric Shipping Tier
    let totalDeadWeight = 0;
    let totalVolumetricWeight = 0;

    pendingItems.forEach((item, index) => {
      const isOverridden = overriddenIndexes.has(index);

      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = item.type.toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      let deadWt = 0;
      let baseVol = 0;
      let packEff = 100;
      let packedVol = 0;

      if (category) {
        deadWt = (category.logisticsConfig?.return?.averageWeightKg || 0);
        baseVol = (category.logisticsConfig?.return?.dimensionalWeightKg || 0);
        packEff = (category.logisticsConfig?.return?.packingEfficiencyPercent || 100);
        packedVol = baseVol / (packEff / 100);

        if (!isOverridden) {
          totalDeadWeight += deadWt;
          totalVolumetricWeight += packedVol;
        }
      }

      collectionItemLog.push({
        name: item.rawItem?.item || item.type,
        quantity: 1,
        deadWeightKg: deadWt,
        baseVolumetricKg: baseVol,
        packingEfficiency: packEff,
        packedVolumetricKg: packedVol,
        isOverridden
      });
    });

    const chargeableWeight = Math.max(totalDeadWeight, totalVolumetricWeight);
    const telemetry = {
      collection: {
        totalDeadWeight,
        totalVolumetricWeight,
        chargeableWeight,
        overriddenSkus: Array.from(overriddenIndexes).map(i => pendingItems[i].type),
        itemLog: collectionItemLog
      }
    };

    if (chargeableWeight > 0) {
      const tierCards = filteredRateCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride)
        .sort((a, b) => a.propertyBag.maxChargeableWeightKg! - b.propertyBag.maxChargeableWeightKg!);
      
      if (tierCards && tierCards.length > 0) {
        const matchedTier = tierCards.find(c => chargeableWeight <= c.propertyBag.maxChargeableWeightKg!) || tierCards[tierCards.length - 1];
        const rawPrice = matchedTier.price;
        const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
        const price = priceInPence / 100;
        services.push({
          rateCard: matchedTier,
          name: matchedTier.name || 'Shipping Tier',
          quantity: 1,
          labels: [`Collection Volumetric Shipping: ${chargeableWeight.toFixed(2)}kg`],
          isInventoryItem: false,
          price,
          total: price,
          itemType: 'Service',
          listSection: 'logistics'
        });
        total += price;
      }
    }

    return { inferredServices: services, runningTotal: total, telemetryData: telemetry };
  }, [triggerValues, filteredRateCards, triggerTypes, selectedUserEquipment, categories]);

  const telemetryString = JSON.stringify(telemetryData);
  React.useEffect(() => {
    if (isSandboxMode && telemetryString) {
      setTelemetry(JSON.parse(telemetryString));
    }
  }, [telemetryString, isSandboxMode, setTelemetry]);

  const handleMacroSelect = (selectedUser: any | null) => {
    categories?.forEach((cat) => {
      methods.setValue(cat.name as any, 0, { shouldValidate: true, shouldDirty: true });
    });
    
    methods.setValue('assets' as any, {}, { shouldValidate: true, shouldDirty: true });

    if (!selectedUser) {
      setSelectedUserEquipment([]);
      return;
    }

    if (equipmentPayload) {
      let userEquipment = equipmentPayload.filter((e: HaloEquipmentDto) => e.user === selectedUser.rawValue);
      
      const primaryTypes = ['laptop', 'desktop', 'pc', 'macbook', 'phone', 'tablet'];
      userEquipment = userEquipment.sort((a: HaloEquipmentDto, b: HaloEquipmentDto) => {
        const aIsPrimary = primaryTypes.some(pt => a.type.toLowerCase().includes(pt)) ? 1 : 0;
        const bIsPrimary = primaryTypes.some(pt => b.type.toLowerCase().includes(pt)) ? 1 : 0;
        return bIsPrimary - aIsPrimary;
      });

      setSelectedUserEquipment(userEquipment);
      
      const serialIndexes: Record<string, number> = {};
      const unmapped: string[] = [];

      for (const item of userEquipment) {
        let normalizedType = item.type?.toLowerCase().trim() || '';
        if (normalizedType.endsWith('s')) normalizedType = normalizedType.slice(0, -1);
        
        let matchFound = false;

        for (const category of categories || []) {
          let cName = category.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          
          if (cName === normalizedType) {
            matchFound = true;
            const t = category.name;
            const currentStepperValue = Number(methods.getValues(t as any)) || 0;
            methods.setValue(t as any, currentStepperValue + 1, { shouldValidate: true, shouldDirty: true });
            
            if (category.isSerialized) {
              const currentAssets = (methods.getValues('assets' as any) as Record<string, any[]>)?.[t] || [];
              const newAssets = [...currentAssets];
              newAssets.push({ makeModel: item.sku || '', serialNumber: item.serial || '', assetTag: '' });
              methods.setValue(`assets.${t}` as any, newAssets, { shouldValidate: true, shouldDirty: true });
            }

            break;
          }
        }
        
        if (!matchFound) {
          unmapped.push(item.type || 'Unknown Item');
        }
      }

      if (unmapped.length > 0) {
        toast(`The following assigned equipment has no matching Category: ${unmapped.join(', ')}`, {
          style: { backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba' }
        });
      }
    }
  };

  const onSubmit = async (data: any) => {
    let duplicateSn = false;
    const seenSns = new Set<string>();

    triggerTypes.forEach((t) => {
      const qty = (data[t] as number) || 0;
      if (qty > 0 && isTypeSerialized(t)) {
        const catAssets = data.assets?.[t] || [];
        for (let i = 0; i < qty; i++) {
          const sn = catAssets[i]?.serialNumber;
          if (sn) {
            const normalizedSn = sn.trim().toLowerCase();
            if (seenSns.has(normalizedSn)) duplicateSn = true;
            seenSns.add(normalizedSn);
          }
        }
      }
    });

    if (duplicateSn) {
      setErrorMessage('Duplicate serial numbers detected. Each serial number must be unique.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const hardwareSelections: Record<string, number> = {};
    triggerTypes.forEach((t) => {
      const quantity = data[t] as number | undefined;
      if (quantity && quantity > 0) {
        hardwareSelections[t] = quantity;
      }
    });

    if (!Object.values(hardwareSelections).some((q: any) => q > 0)) {
      setErrorMessage('You must select at least one item to collect to submit an Offboard request.');
      return;
    }

    const devicesToBeCollected: string[] = [];
    
    triggerTypes.forEach((t) => {
      const qty = (data[t] as number) || 0;
      if (qty > 0) {
        if (isTypeSerialized(t)) {
          const catAssets = data.assets?.[t] || [];
          for (let i = 0; i < qty; i++) {
            const asset = catAssets[i];
            if (asset) {
              const tag = asset.assetTag ? asset.assetTag.trim() : '';
              devicesToBeCollected.push(`${t}: ${asset.makeModel}, Serial: ${asset.serialNumber}, Tag: ${tag || 'none'}`);
            }
          }
        } else {
          devicesToBeCollected.push(`${qty}x ${t}`);
        }
      }
    });

    setCheckoutPayload({
      hardwareSelections,
      inferredServices,
      runningTotal,
      devicesToBeCollected,
      userName: data.userName,
      userEmail: data.userEmail,
      personalEmail: data.personalEmail,
      userMobile: data.userMobile,
      noCorporateEmail: data.noCorporateEmail
    });

    modalProps.open();
    setLoading(false);
  };

  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const resetForm = useCallback(() => {
    const defaults: any = {
      macroAction: null,
      assets: {},
      userName: '',
      userEmail: '',
      personalEmail: '',
      userMobile: '',
      noCorporateEmail: false
    };
    categories?.forEach((cat) => {
      defaults[cat.name] = 0;
    });
    
    methods.reset(defaults);
    setSelectedUserEquipment([]);
  }, [methods, categories]);

  const isDirty = methods.formState.isDirty;
  useDirtyFormGuard(isDirty);

  return (
    <>
      <UnsavedChanges 
        show={isDirty} 
        title="Abandon Request"
        message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
        confirmText="Yes, Abandon"
        confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
        cancelText="No, Continue"
        cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
      />
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col gap-2 items-start">
            <h3 className="text-3xl font-bold">Offboard User</h3>
          </div>
          {isAdmin && (
            isSandboxMode ? (
              <button
                type="button"
                onClick={() => navigate(`/msps/${partnerId}/companies/${companyId}/management/offboard`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 shadow-sm"
              >
                <span>🚪</span> Exit Sandbox
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/admin/sandbox/partners/${partnerId}/companies/${companyId}/offboard-user`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200 shadow-sm"
              >
                <span>⚙️</span> Debug in Sandbox
              </button>
            )
          )}
        </div>
      </BreadcrumbPageHeader>
      
      <Form methods={methods as any} onSubmit={onSubmit}>
        <ServiceRequestLayout
          title=""
          macroSlot={
            <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-full">
                <SmartUserSelector 
                  methods={methods as any} 
                  uniqueUsers={uniqueUsers} 
                  onSelect={handleMacroSelect} 
                  nameField="userName" 
                  corpEmailField="userEmail" 
                  personalEmailLabel="Personal Email"
                  personalEmailOptional={false}
                  mobileNumberField="userMobile"
                  mobileNumberLabel="Personal Mobile Number"
                  mobileNumberOptional={false}
                />
              </div>
              {selectedUserEquipment.length > 0 && (
                <div className="mt-4 bg-gray-50 p-3 rounded-md border text-sm w-full">
                  <h4 className="font-semibold text-gray-700 mb-2">Assigned Equipment</h4>
                  <ul className="space-y-1 text-gray-600">
                    {selectedUserEquipment.map((item, idx) => (
                      <li key={idx}>
                        &bull; {item.type} ({item.sku})
                        {item.serial ? ` - SN: ${item.serial}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          }
          leftColumn={
            <div className="space-y-4">
              <HardwareCollectionSection triggerTypes={triggerTypes} deviceTriggers={deviceTriggers} peripheralTriggers={peripheralTriggers} handleTriggerChange={handleTriggerChange} isTypeSerialized={isTypeSerialized} methods={methods} />
            </div>
          }
          rightColumn={
            <div className="sticky top-4 space-y-4">
              <div className="border p-4 rounded bg-gray-50 shadow-sm space-y-4">
                <h4 className="text-xl font-bold border-b pb-2">Basket</h4>
                
                {inferredServices.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No hardware selected</p>
                ) : (
                  <div className="space-y-4">
                    {/* Zero-Cost Inventory Items */}
                    {inferredServices.filter(s => s.isInventoryItem).length > 0 && (
                      <div className="space-y-3">
                        <h5 className="text-sm font-semibold text-gray-500 uppercase">Hardware to Collect</h5>
                        {inferredServices.filter(s => s.isInventoryItem).map((service, idx) => (
                          <div key={`inv-${idx}`} className="p-3 bg-white border rounded">
                            <div className="flex justify-between items-start text-sm">
                              <div className="pr-4">
                                <span className="font-medium">{service.quantity}x</span> {service.name}
                              </div>
                              <div className="font-medium whitespace-nowrap italic"></div>
                            </div>
                            {service.hasLifecyclePack && (
                              <span className="italic text-green-600 text-xs block mt-1">
                                Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {(() => {
                      const adminFees = inferredServices.filter(s => s.listSection === 'admin');
                      const logisticsItems = inferredServices.filter(s => s.listSection === 'logistics');
                      const processingItems = inferredServices.filter(s => s.listSection === 'processing');

                      const renderService = (service: any, idx: string | number) => {
                        const associatedPhysicalItems = (service as any).associatedPhysicalItems as Array<{ type: string, hasLifecyclePack: boolean, suppressSerialInput?: boolean }>;
                        const physicalTypeGroups: Record<string, { count: number, type: string }> = {};
                        
                        if (associatedPhysicalItems) {
                          associatedPhysicalItems.forEach(item => {
                            if (!physicalTypeGroups[item.type]) physicalTypeGroups[item.type] = { count: 0, type: item.type };
                            physicalTypeGroups[item.type].count++;
                          });
                        }

                        return (
                          <div key={`${service.rateCard?.id || 'id'}-${service.hasLifecyclePack}-${idx}`} className="p-3 bg-white border rounded">
                            <div className="flex justify-between items-start text-sm">
                              <div className="pr-4">
                                <span className="font-medium">{service.quantity}x</span> {service.name}
                              </div>
                              <div className="font-medium whitespace-nowrap">
                                {formatCurrency(((service.rateCard?.price?.price || 0) / 100) * service.quantity, company?.region?.code)}
                              </div>
                            </div>
                            {service.hasLifecyclePack && (
                              <span className="italic text-green-600 text-xs block mt-1">
                                Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                              </span>
                            )}

                            {associatedPhysicalItems && Object.keys(physicalTypeGroups).length > 0 && (
                              <div className="mt-2 space-y-1 pt-2 border-t border-gray-100 pl-4 border-l-2 border-l-blue-200">
                                {Object.values(physicalTypeGroups).map((group, groupIdx) => (
                                  <div key={groupIdx}>
                                    <p className="text-sm font-medium text-gray-700">{group.count}x {group.type}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return (
                        <>
                          {adminFees.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                                Administration
                                <InfoTooltip id="admin-info" text={getAdminTooltipText(company?.name ?? 'Company')} place="left-start" className="!max-w-none" />
                              </h5>
                              {adminFees.map((service, idx) => renderService(service, `admin-${idx}`))}
                            </div>
                          )}
                          {logisticsItems.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                                Logistics & Insurance
                                <InfoTooltip id="logistics-info" text={logisticsTooltipText} place="left" className="!max-w-none" />
                              </h5>
                              {logisticsItems.map((service, idx) => renderService(service, `log-${idx}`))}
                            </div>
                          )}
                          {processingItems.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                                Processing
                                <InfoTooltip id="processing-info" text={processingTooltipText} place="left" className="!max-w-none" />
                              </h5>
                              {processingItems.map((service, idx) => renderService(service, `proc-${idx}`))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 text-lg font-bold border-t border-gray-300">
                  <span>Total Estimate</span>
                  <span>{formatCurrency(runningTotal, company?.region?.code)}</span>
                </div>

                {errorMessage && (
                  <div className="text-red-600 text-sm mt-4 bg-red-50 p-3 rounded border border-red-200">
                    {errorMessage}
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-2">
                  <Button variant={ButtonVariant.Primary} className="w-full justify-center" type="submit" loading={loading}>
                    Continue
                  </Button>
                  <Button
                    variant={ButtonVariant.Secondary}
                    className="w-full justify-center"
                    onClick={resetForm}
                    disabled={loading}
                    type="button"
                  >
                    Clear Form
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      </Form>

      {checkoutPayload && (
        <OffboardUserCollectionModal
          {...modalProps}
          mspId={partnerId}
          customerId={companyId}
          customerName={company.name}
          hardwareSelections={checkoutPayload.hardwareSelections}
          inferredServices={checkoutPayload.inferredServices}
          runningTotal={checkoutPayload.runningTotal}
          devicesToBeCollected={checkoutPayload.devicesToBeCollected}
          userName={checkoutPayload.userName}
          userEmail={checkoutPayload.userEmail}
          personalEmail={checkoutPayload.personalEmail}
          userMobile={checkoutPayload.userMobile}
          noCorporateEmail={checkoutPayload.noCorporateEmail}
          uniqueUsers={uniqueUsers}
          onSuccessCallback={() => {
            resetForm();
            toast.success('Service Request Submitted Successfully');
            setTimeout(() => {
              navigate(`/msps/${partnerId}/companies/${companyId}/management/submittedRequests`);
            }, 0);
          }}
        />
      )}
    </>
  );
};
